/**
 * Reglas de estado del inventario — fuente única (PROMPT_N°054).
 *
 * Por qué vive acá: el estado de un ingrediente ("Crítico", "Sin uso") lo
 * calculaba sólo `Ingredientes.tsx`, en medio del JSX de la tabla. Los
 * reportes necesitan exactamente el mismo veredicto —y además en SQL, para
 * poder filtrar y agrupar sin traerse 307 filas a Node—, así que la regla
 * queda escrita una vez y el backend tiene su espejo con un test que los
 * compara. Es la lección del N°053 con las etiquetas de movimiento: dos
 * copias de la misma regla divergen, y la que se ve en pantalla deja de
 * coincidir con la que se exporta.
 */
/**
 * Un ingrediente "bajo" está entre el mínimo y 1,5× el mínimo. El factor es
 * el que ya usaba la pantalla; queda nombrado para que el espejo SQL no lo
 * repita como número suelto.
 */
export const FACTOR_STOCK_BAJO = 1.5;
export const estadoStock = ({ stockActual, stockMinimo, haEstadoEnBodega, }) => {
    // Catálogo que nunca entró a bodega no está en crisis: no tiene stock
    // porque nunca tuvo, no porque se haya consumido. Pintarlo en rojo era lo
    // que llenaba la pantalla de alertas falsas (PROMPT_N°052).
    if (haEstadoEnBodega === false && stockActual <= 0)
        return 'sin_uso';
    if (stockActual <= stockMinimo)
        return 'critico';
    if (stockActual <= stockMinimo * FACTOR_STOCK_BAJO)
        return 'bajo';
    return 'ok';
};
export const ETIQUETAS_ESTADO_STOCK = {
    sin_uso: 'Sin uso',
    critico: 'Crítico',
    bajo: 'Bajo',
    ok: 'OK',
};
export const ETIQUETAS_ESTADO_VENCIMIENTO = {
    vencido: 'Vencido',
    por_vencer: 'Vence en ≤ 7 días',
    proximo: 'Vence en ≤ 30 días',
    ok: 'Vigente',
    sin_fecha: 'Sin fecha',
};
export const DIAS_POR_VENCER = 7;
export const DIAS_PROXIMO = 30;
/**
 * Semáforo a partir de los días que faltan. Se recibe el número de días ya
 * calculado —y no dos fechas— para no re-resolver acá la zona horaria: el
 * cálculo de "hoy en Chile" vive en `dateUtils` (frontend) y en
 * `fechaChile` (backend), y duplicarlo es cómo volvió el bug del N°048.
 *
 * `null` es "sin fecha de vencimiento", que NO es lo mismo que vencido: un
 * abarrote sin control de vencimiento no debe salir en rojo.
 */
export const estadoVencimiento = (diasRestantes) => {
    if (diasRestantes === null || diasRestantes === undefined)
        return 'sin_fecha';
    if (diasRestantes < 0)
        return 'vencido';
    if (diasRestantes <= DIAS_POR_VENCER)
        return 'por_vencer';
    if (diasRestantes <= DIAS_PROXIMO)
        return 'proximo';
    return 'ok';
};
/**
 * Subtítulo del número. Vive en shared porque lo imprimen la tarjeta de
 * Ingredientes, dos reportes, el Excel y el modal: un valor de inventario sin
 * decir con qué método se calculó es un número que nadie puede auditar, y
 * cinco redacciones distintas del mismo método son peor que ninguna.
 */
export const leyendaValorizacion = (r) => {
    const plural = (n, s) => `${n} ${s}${n === 1 ? '' : 's'}`;
    const partes = [];
    if (r.lotes_a_referencia > 0) {
        partes.push(`${plural(r.lotes_a_referencia, 'lote')} a precio de referencia`);
    }
    if (r.ingredientes_sin_costo > 0) {
        partes.push(`${plural(r.ingredientes_sin_costo, 'ingrediente')} sin costo`);
    }
    if (r.stock_sin_lote > 0) {
        partes.push(`${plural(r.stock_sin_lote, 'ingrediente')} con stock sin lote`);
    }
    return partes.length > 0
        ? `Valorizado al costo de lote (${partes.join(', ')})`
        : 'Valorizado al costo de lote';
};
